import { ComponentType, useEffect, useState } from 'react';
import useStudioGroup from '../../../hooks/useStudioGroup';

const BASE_THRESHOLD = 32;
const OFFSET = 80;
// Max height diff is 314px on BAO, so 2px larger than offset + base threshold to avoid flicker
//  issues
const OFFSET_BAO = 286;

export interface WithScrollThresholdProps {
  pastThreshold: boolean;
  pastThresholdForSidebar?: boolean;
}

export function withScrollThreshold<T extends WithScrollThresholdProps>(
  WrappedComponent: ComponentType<T>,
  withMobileSidebar = false,
) {
  return function ScrollAwareComponent(props: Omit<T, keyof WithScrollThresholdProps>) {
    const studioGroup = useStudioGroup();

    const [sidebarContainer, setSidebarContainer] = useState<HTMLElement | null>(null);
    const [pastThreshold, setPastThreshold] = useState(false);
    const [pastThresholdForSidebar, setPastThresholdForSidebar] = useState(false);

    // Observe DOM for sidebar component, can be opened and closed
    useEffect(() => {
      if (!withMobileSidebar) return;

      const found = document.querySelector('.material-page .sidebar-layout');
      setSidebarContainer(found as HTMLElement | null);

      const observer = new MutationObserver(() => {
        const found = document.querySelector('.material-page .sidebar-layout');
        setSidebarContainer(found as HTMLElement | null);
      });

      observer.observe(document.body, { childList: true, subtree: true });

      return () => observer.disconnect();
    }, []);

    useEffect(() => {
      const mainContainer = document.querySelector('main');
      if (!mainContainer) return;

      const growThreshold = BASE_THRESHOLD + (studioGroup === 'BAO' ? OFFSET_BAO : OFFSET);

      const handleScroll = () => {
        const { scrollTop } = mainContainer;

        if (scrollTop > growThreshold) {
          setPastThreshold(true);
        } else if (scrollTop < BASE_THRESHOLD) {
          setPastThreshold(false);
        }
      };

      // disable scroll anchoring on the scroll container
      mainContainer.style.overflowAnchor = 'none';
      mainContainer.addEventListener('scroll', handleScroll, { passive: true });

      handleScroll();

      return () => {
        mainContainer.style.overflowAnchor = '';
        mainContainer.removeEventListener('scroll', handleScroll);
      };
    }, [studioGroup]);

    useEffect(() => {
      if (!withMobileSidebar || !sidebarContainer) return;

      const handleScroll = () => {
        const { scrollTop } = sidebarContainer;

        if (scrollTop > BASE_THRESHOLD + OFFSET) {
          setPastThresholdForSidebar(true);
        } else if (scrollTop < BASE_THRESHOLD) {
          setPastThresholdForSidebar(false);
        }
      };

      // disable scroll anchoring on the scroll container
      sidebarContainer.style.overflowAnchor = 'none';
      sidebarContainer.addEventListener('scroll', handleScroll, { passive: true });

      handleScroll();

      return () => {
        sidebarContainer.style.overflowAnchor = '';
        sidebarContainer.removeEventListener('scroll', handleScroll);
      };
    }, [sidebarContainer]);

    return (
      <WrappedComponent
        {...(props as T)}
        pastThreshold={pastThreshold}
        pastThresholdForSidebar={pastThresholdForSidebar}
      />
    );
  };
}
